import { AbstractService } from "./ApiClient";
import { ApiRoutes } from "@typingsGenerator/ApiRoutes";
import ProductListDto = Cloud.RavenDB.Core.Dto.Products.ProductListDto;
import ProductDto = Cloud.RavenDB.Core.Dto.Products.ProductDto;
import ProductCreateDto = Cloud.RavenDB.Core.Dto.Products.ProductCreateDto;
import ProductCreateContextDto = Cloud.RavenDB.Core.Dto.Products.ProductCreateContextDto;
import ProductsStatusDto = Cloud.RavenDB.Core.Dto.Products.ProductsStatusDto;
import UpdateAllowedIpsDto = Cloud.RavenDB.Core.Dto.Products.UpdateAllowedIpsDto;
import PowerBiFeatureConfigurationDto = Cloud.RavenDB.Core.Dto.Products.PowerBiFeatureConfigurationDto;
import AutoscalingFeatureConfigurationDto = Cloud.RavenDB.Core.Dto.Products.AutoscalingFeatureConfigurationDto;
import ExtendProductStorageVolumeDto = Cloud.RavenDB.Core.Dto.Products.ExtendProductStorageVolumeDto;
import ChangeProductInstanceTypeDto = Cloud.RavenDB.Core.Dto.Products.ChangeProductInstanceTypeDto;
import ValidateAllowedIpsDto = Cloud.RavenDB.Core.Dto.Products.ValidateAllowedIpsDto;
import ProductStatusItemDto = Cloud.RavenDB.Core.Dto.Products.ProductStatusItemDto;
import ProductPaymentContextDto = Cloud.RavenDB.Core.Dto.Costs.ProductPaymentContextDto;
import ProductFeatureName = Cloud.RavenDB.Core.Models.ProductFeatureName;
import UpdateMonitoringIpsDto = Cloud.RavenDB.Core.Dto.Products.UpdateMonitoringIpsDto;
import OpenTelemetrySettingsDto = Cloud.RavenDB.Core.Dto.Products.OpenTelemetrySettingsDto;
import { downloadBlobAsFile } from "@commonClient/src/utils/fileUtils";

export default class ProductsService extends AbstractService {
    getProducts = async (domainName: string): Promise<ProductListDto> => {
        const products = await this.query<ProductListDto>(ApiRoutes.products.getProducts({ domainName }));
        return products.data;
    };

    getProductsStatus = async (domainName: string): Promise<ProductsStatusDto> => {
        const status = await this.query<ProductsStatusDto>(ApiRoutes.products.getProductsStatus({ domainName }));
        return status.data;
    };

    getProductStatus = async (domainName: string, id: string): Promise<ProductStatusItemDto> => {
        const allStatuses = await this.getProductsStatus(domainName);
        return allStatuses.items.find((x) => x.productId === id);
    };

    getProduct = async (domainName: string, id: string): Promise<ProductDto> => {
        const product = await this.query<ProductDto>(ApiRoutes.products.getProduct({ domainName, id }));
        return product.data;
    };

    restartInstance = async (domainName: string, id: string, nodeTag: string): Promise<void> => {
        await this.post<void>(ApiRoutes.products.restartNode({ domainName, id, nodeTag }));
    };

    installTools = async (domainName: string, id: string): Promise<void> => {
        await this.post<void>(ApiRoutes.products.installTools({ domainName, id }));
    };

    terminateInstance = async (domainName: string, id: string): Promise<void> => {
        await this.post<void>(ApiRoutes.products.terminateProduct({ domainName, id }));
    };

    deleteInstance = async (domainName: string, id: string): Promise<void> => {
        await this.post<void>(ApiRoutes.products.deleteProduct({ domainName, id }));
    };

    getProductCreateContext = async (domainName: string): Promise<ProductCreateContextDto> => {
        const context = await this.query<ProductCreateContextDto>(ApiRoutes.products.getCreateContext({ domainName }));
        return context.data;
    };

    updateAllowedIps = async (domainName: string, dto: UpdateAllowedIpsDto): Promise<void> => {
        await this.post<UpdateAllowedIpsDto, void>(ApiRoutes.products.updateAllowedIps({ domainName }), dto);
    };

    updateMonitoringIps = async (domainName: string, dto: UpdateMonitoringIpsDto): Promise<void> => {
        await this.post<UpdateMonitoringIpsDto, void>(ApiRoutes.products.updateMonitoringIps({ domainName }), dto);
    };

    rollSnmpCredentials = async (domainName: string, id: string): Promise<void> => {
        await this.post<void>(ApiRoutes.products.rollSnmpCredentials({ domainName, id }));
    };

    validateCidrs = async (dto: ValidateAllowedIpsDto): Promise<void> => {
        await this.post<ValidateAllowedIpsDto, void>(ApiRoutes.products.validateCidrs(), dto);
    };

    createProduct = async (dto: ProductCreateDto): Promise<void> => {
        await this.post<ProductCreateDto, void>(ApiRoutes.products.createProduct(), dto);
    };

    updateStorage = async (domainName: string, dto: ExtendProductStorageVolumeDto) => {
        await this.post<ExtendProductStorageVolumeDto, void>(ApiRoutes.products.changeVolume({ domainName }), dto);
    };

    updateInstanceType = async (domainName: string, dto: ChangeProductInstanceTypeDto) => {
        await this.post<ChangeProductInstanceTypeDto, void>(ApiRoutes.products.changeProduct({ domainName }), dto);
    };

    paymentContext = async (domainName: string, dto: ProductCreateDto) => {
        const context = await this.post<ProductCreateDto, ProductPaymentContextDto>(
            ApiRoutes.products.getPaymentContext({ domainName }),
            dto
        );
        return context.data;
    };

    enableFeature = async (domainName: string, id: string, feature: ProductFeatureName) => {
        await this.post<void>(ApiRoutes.products.enableFeature({ domainName, id, feature }));
    };

    disableFeature = async (domainName: string, id: string, feature: ProductFeatureName) => {
        await this.delete<void>(ApiRoutes.products.disableFeature({ domainName, id, feature }));
    };

    updateFeatureConfiguration_PowerBi = async (domainName: string, dto: PowerBiFeatureConfigurationDto) => {
        await this.post<PowerBiFeatureConfigurationDto, void>(
            ApiRoutes.products.updateFeatureConfiguration_PowerBi({ domainName }),
            dto
        );
    };

    updateFeatureConfiguration_Autoscaling = async (domainName: string, dto: AutoscalingFeatureConfigurationDto) => {
        await this.post<AutoscalingFeatureConfigurationDto, void>(
            ApiRoutes.products.updateFeatureConfiguration_Autoscaling({ domainName }),
            dto
        );
    };

    downloadCertificate = async (domainName: string, productId: string) => {
        const response = await this.queryBlob(ApiRoutes.products.getClientCertificate({ domainName, id: productId }));

        const contentDisposition = response.headers["content-disposition"];
        let filename = "certificates.zip";
        if (contentDisposition && contentDisposition.includes("filename=")) {
            const match = contentDisposition.match(/filename="?([^"]+)"?;/);
            if (match?.[1]) {
                filename = match[1];
            }
        }

        const blob = new Blob([response.data]);
        downloadBlobAsFile(filename, blob);
    };

    regenerateClientCertificate = async (domainName: string, productId: string) => {
        await this.post<void>(ApiRoutes.products.regenerateClientCertificate({ domainName, id: productId }));
    };

    addAdditionalNode = async (domainName: string, id: string) => {
        await this.post<void>(ApiRoutes.products.addAdditionalNode({ domainName, id }));
    };

    removeAdditionalNode = async (domainName: string, id: string, nodeTag: string) => {
        await this.post<void>(ApiRoutes.products.removeAdditionalNode({ domainName, id, nodeTag }));
    };

    updateOpenTelemetrySettings = async (domainName: string, dto: OpenTelemetrySettingsDto) => {
        await this.post<OpenTelemetrySettingsDto, void>(
            ApiRoutes.products.updateFeatureConfiguration_MonitoringOpenTelemetry({ domainName }),
            dto
        );
    };
}
